import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Box, TextField, Button, Dialog, DialogContent, DialogTitle,
  Grid, MenuItem, IconButton, InputAdornment, Typography, Tabs, Tab,
  Paper, TableContainer, TableBody, TableHead, Table, TableCell, TableRow,
  Autocomplete, Tooltip, Checkbox, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { AuthContext } from '..';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import format from 'date-fns/format';
import SalesHistory from './saleshistory';
import ReusableTable from '../components/tables/resuabletable';
import Swal from 'sweetalert2';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function Sales() {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSaleId, setEditingSaleId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [pdf] = useState(true);
  const { companyid, companydata, token } = useContext(AuthContext);
  const [dialogwidth, setDialogwidth] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 3,
  });
  const [totalrows, setTotalrows] = useState(0);
  const [opencolspaper, setOpencolspaper] = useState(false);
  const [columnOrder, setColumnOrder] = useState([]);
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [invoicedialog, setInvoicedialog] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);


  const handlePaginationChange = (paginationData) => {
    setPagination(paginationData);
  };

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date(),
    customerId: '',
    createdBy: '',
    services: [],
    totalAmount: 0,
    paidAmount: 0,
    currentlyPaying: 0,
    remainingBalance: 0,
    finalTotalAmount: 0,
    revisedAmounts: [],
  });

  useEffect(() => {
    if (!companyid) {
      Swal.fire('Error', 'Company ID is not set. Please log in again.', 'error');
      return;
    }
    fetchSales();
    fetchCustomers();
    fetchEmployees();
  }, [appliedFilters, pagination, companyid]);

  const fetchSales = () => {
    const payload = {
      filters: JSON.stringify(appliedFilters),
      currentpage: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
    };
    axios
      .get(`${process.env.REACT_APP_API_LINKS}/sales/clientspayment/${companyid}`, {
        params: payload,
        withCredentials: true,
      })
      .then((res) => {
        if (!res.data) {
          console.error('No data returned from API');
          setColumns([]);
          setRows([]);
          setTotalrows(0);
          Swal.fire('Error', 'No sales data returned from server.', 'error');
          return;
        }
        let columns = res.data.columns || [];
        const data = res.data.data || [];

        const formattedRows = data.map((row) => {
          let finalTotalAmount = row.amount_to_pay || 0;
          let revisedAmounts = [];
          try {
            revisedAmounts = JSON.parse(row.revisedAmounts || '[]');
            const finalAmountEntry = revisedAmounts.find(
              (entry) => entry.description === 'Final Total Amount'
            );
            finalTotalAmount = finalAmountEntry ? parseFloat(finalAmountEntry.amount) : row.amount_to_pay;
          } catch (err) {
            console.error(`Error parsing revisedAmounts for row ${row.id}:`, err);
          }
          return {
            ...row,
            finalTotalAmount,
            revisedAmounts,
            display_invoice_date: row.invoice_date ? format(new Date(row.invoice_date), 'dd/MM/yyyy') : '',
          };
        });
        setColumns(columns);
        setColumnOrder(columns.filter(col => !col.hide).map(col => col.field));
        setRows(formattedRows);
        setTotalrows(res.data.totalrows?.[0]?.totalusers || 0);
      })
      .catch((err) => {
        console.error('Failed to fetch sales:', err.response?.data || err.message);
        setColumns([]);
        setRows([]);
        setTotalrows(0);
        Swal.fire('Error', 'Failed to fetch sales data. Please check the server connection.', 'error');
      });
  };

  const fetchCustomers = () => {
    axios
      .get(`${process.env.REACT_APP_API_LINKS}/clients/getcompanyclients/${companyid}`, { withCredentials: true })
      .then((res) => {
        const formatted = (res.data.data || []).map((row) => ({
          id: row.id,
          name: row.client_name,
          contactPerson: row.contact_person,
          email: row.email,
          phone: row.phone,
          services: row.services,
          revisedAmounts: row.revisedAmounts,
        }));
        setCustomers(formatted);
      })
      .catch((err) => {
        console.error("Failed to fetch customers:", err);
        setCustomers([]);
      });
  };

  const fetchEmployees = () => {
    axios
      .get(`${process.env.REACT_APP_API_LINKS}/employees/getemployees`, {
        headers: {
          Authorization: `bearer ${token}`
        },
        withCredentials: true
      })
      .then((res) => {
        setEmployees(res.data.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch employees:", err);
        setEmployees([]);
      });
  };

  const handleCustomerSelect = async (e, value) => {
    if (value) {
      let customerServices = [];
      let revisedAmounts = [];

      try {
        customerServices = typeof value.services === 'string'
          ? JSON.parse(value.services || '[]')
          : Array.isArray(value.services) ? value.services : [];
      } catch {
        customerServices = [];
      }

      try {
        revisedAmounts = typeof value.revisedAmounts === 'string'
          ? JSON.parse(value.revisedAmounts || '[]')
          : Array.isArray(value.revisedAmounts) ? value.revisedAmounts : [];
      } catch (err) {
        console.error("Error parsing revisedAmounts:", err);
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_LINKS}/sales/getcustomerpayments/${value.id}`,
          {
            headers:
            {
              Authorization: `bearer ${token}`
            },
            withCredentials: true
          }
        );
        const paymentHistory = response.data.paymentHistory || [];
        const finalAmountEntry = revisedAmounts.find(
          (entry) => entry.description === 'Final Total Amount'
        );
        const finalTotalAmount = finalAmountEntry ? parseFloat(finalAmountEntry.amount) : 0;

        const formatted = customerServices.map((service, index) => ({
          id: service.id,
          service: service.id,
          name: service.service_name || `Service ${index + 1}`,
          totalAmount: parseFloat(service.total_amount) || 0,
          start_date: service.start_date || '',
          end_date: service.end_date || '',
        }));

        const total = formatted.reduce((a, v) => a + v.totalAmount, 0);
        const paid = paymentHistory.reduce((a, v) => a + (parseFloat(v.total_paid) || 0), 0);

        setFormData((prev) => ({
          ...prev,
          customerId: value.id,
          services: formatted,
          totalAmount: total,
          paidAmount: paid,
          currentlyPaying: 0,
          remainingBalance: finalTotalAmount - paid,
          finalTotalAmount,
          revisedAmounts,
        }));
      } catch (err) {
        console.error("Failed to fetch payment history:", err);
        const formatted = customerServices.map((service, index) => ({
          id: service.id,
          service: service.id,
          name: service.service_name || `Service ${index + 1}`,
          totalAmount: parseFloat(service.total_amount) || 0,
          start_date: service.start_date || '',
          end_date: service.end_date || '',
        }));

        const total = formatted.reduce((a, v) => a + v.totalAmount, 0);
        const finalTotalAmount = revisedAmounts.find(
          (entry) => entry.description === 'Final Total Amount'
        )?.amount || total;

        setFormData((prev) => ({
          ...prev,
          customerId: value.id,
          services: formatted,
          totalAmount: total,
          paidAmount: 0,
          currentlyPaying: 0,
          remainingBalance: finalTotalAmount,
          finalTotalAmount,
          revisedAmounts,
        }));
      }
    } else {
      setDialogwidth(false);
      setFormData((prev) => ({
        ...prev,
        customerId: '',
        services: [],
        totalAmount: 0,
        paidAmount: 0,
        currentlyPaying: 0,
        remainingBalance: 0,
        finalTotalAmount: 0,
        revisedAmounts: [],
      }));
    }
  };

  const handleEdit = async (sale) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_LINKS}/sales/getcustomerpayments/${sale.customer_id}`,
        {
          headers:
          {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      );

      const servicePayments = response.data.servicePayments || [];
      let finalTotalAmount = sale.amount_to_pay || 0;
      let revisedAmounts = [];
      try {
        revisedAmounts = JSON.parse(sale.revisedAmounts || '[]');
        const finalAmountEntry = revisedAmounts.find(
          (entry) => entry.description === 'Final Total Amount'
        );
        finalTotalAmount = finalAmountEntry ? parseFloat(finalAmountEntry.amount) : sale.amount_to_pay;
      } catch (err) {
        console.error("Error parsing revisedAmounts:", err);
      }

      const formattedServices = (sale.services || []).map((service, index) => ({
        id: index,
        name: service.service_name,
        totalAmount: parseFloat(service.total_amount) || 0,
        start_date: service.start_date || '',
        end_date: service.end_date || '',
      }));

      const totalAmount = formattedServices.reduce((sum, s) => sum + s.totalAmount, 0);
      const paidAmount = servicePayments.reduce(
        (sum, sp) => sum + (parseFloat(sp.total_paid) || 0),
        0
      );
      const remainingBalance = finalTotalAmount - paidAmount;

      const matchedCustomer = customers.find((c) => c.id === sale.customer_id) || null;
      const matchedEmployee = employees.find((e) => e.id === sale.created_by) || null;

      setFormData({
        invoiceNumber: sale.invoice_number || '',
        invoiceDate: sale.invoice_date ? new Date(sale.invoice_date) : new Date(),
        customerId: matchedCustomer?.id || '',
        createdBy: matchedEmployee?.id || '',
        services: formattedServices,
        totalAmount,
        paidAmount,
        currentlyPaying: 0,
        remainingBalance,
        finalTotalAmount,
        revisedAmounts,
      });

      setEditingSaleId(sale.id);
      setOpenDialog(true);
    } catch (err) {
      console.error("Failed to fetch payment history:", err);
      let finalTotalAmount = sale.amount_to_pay || 0;
      let revisedAmounts = [];
      try {
        revisedAmounts = JSON.parse(sale.revisedAmounts || '[]');
        const finalAmountEntry = revisedAmounts.find(
          (entry) => entry.description === 'Final Total Amount'
        );
        finalTotalAmount = finalAmountEntry ? parseFloat(finalAmountEntry.amount) : sale.amount_to_pay;
      } catch (err) {
        console.error("Error parsing revisedAmounts:", err);
      }

      const formattedServices = (sale.services || []).map((service, index) => ({
        id: index,
        name: service.service_name,
        totalAmount: parseFloat(service.total_amount) || 0,
        start_date: service.start_date || '',
        end_date: service.end_date || '',
      }));

      const totalAmount = formattedServices.reduce((sum, s) => sum + s.totalAmount, 0);
      const paidAmount = 0;
      const remainingBalance = finalTotalAmount - paidAmount;

      const matchedCustomer = customers.find((c) => c.id === sale.customer_id) || null;
      const matchedEmployee = employees.find((e) => e.id === sale.created_by) || null;

      setFormData({
        invoiceNumber: sale.invoice_number || '',
        invoiceDate: sale.invoice_date ? new Date(sale.invoice_date) : new Date(),
        customerId: matchedCustomer?.id || '',
        createdBy: matchedEmployee?.id || '',
        services: formattedServices,
        totalAmount,
        paidAmount,
        currentlyPaying: 0,
        remainingBalance,
        finalTotalAmount,
        revisedAmounts,
      });

      setEditingSaleId(sale.id);
      setOpenDialog(true);
    }
  };

  const handlePaymentChange = (amount) => {
    const entered = parseFloat(amount) || 0;
    const max = formData.finalTotalAmount - formData.paidAmount;
    const safe = Math.min(Math.max(entered, 0), max);

    setFormData({
      ...formData,
      currentlyPaying: safe,
      remainingBalance: formData.finalTotalAmount - formData.paidAmount - safe,
    });
  };

  const handleDelete = (sale) => {
    Swal.fire({
      title: `Are you sure you want to delete invoice "${sale.invoice_number}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${process.env.REACT_APP_API_LINKS}/sales/deletesale/${sale.id}`)
          .then(() => {
            Swal.fire('Deleted!', 'Invoice has been deleted.', 'success');
            fetchSales();
          })
          .catch(() => {
            Swal.fire('Error!', 'Failed to delete invoice.', 'error');
          });
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, invoiceDate: date }));
  };

  const handleSubmit = () => {
    const payload = {
      ...formData,
      invoiceDate: format(formData.invoiceDate, 'yyyy-MM-dd'),
      companyid,
      services: formData.services.map((s) => ({
        serviceid: s.id,
        service_name: s.name,
        total_amount: s.totalAmount,
        start_date: s.start_date,
        end_date: s.end_date,
      })),
      previously_paid: formData.paidAmount,
      currently_paying: formData.currentlyPaying,
      remaining_balance: formData.remainingBalance,
      revisedAmounts: JSON.stringify(formData.revisedAmounts),
    };

    const apiCall = editingSaleId
      ? axios.put(`${process.env.REACT_APP_API_LINKS}/sales/updatesale/${editingSaleId}`, payload,
        {
          headers:
          {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      )
      : axios.post(`${process.env.REACT_APP_API_LINKS}/sales/addsale`, payload, {
        headers:
        {
          Authorization: `bearer ${token}`
        },
        withCredentials: true
      });

    apiCall
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: editingSaleId ? 'Sale updated successfully' : 'Sale recorded successfully',
          timer: 1500,
        });
        setOpenDialog(false);
        setEditingSaleId(null);
        fetchSales();
      })
      .catch((err) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: editingSaleId ? 'Failed to update sale' : 'Failed to record sale',
          timer: 2000,
        });
        console.error("Sale submission error:", err);
      });
  };

  const filteredColumns = columnOrder
    .map(field => columns.find(col => col.field === field))
    .filter(col => col && !col.hide);

  const exportToExcel = () => {
    // Decide which rows to export
    const rowsToExport = selectedIds.length > 0
      ? filteredRows.filter(row => selectedIds.includes(row.id))
      : filteredRows;

    // Format data like your original logic
    const exportData = rowsToExport.map((row, index) => {
      const formattedRow = { 'S.No': index + 1 };

      filteredColumns.forEach((col) => {
        if (col.headerName === 'S.No' || col.headerName === 'S.NO') return;

        formattedRow[col.headerName || col.field] = row[col.field] || '';
      });

      return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales');
    XLSX.writeFile(
      workbook,
      `sales_data_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };


  const handlePdf = async (sale, action, dynamicSubject) => {
    if (!action || action === 'cancel') return;

    try {
      const bankRes = await axios.get(
        `${process.env.REACT_APP_API_LINKS}/settings/bank-account`,
        {
          headers:
          {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      );

      const bankData = bankRes.data?.bankData;

      const settingsRes = await axios.get(
        `${process.env.REACT_APP_API_LINKS}/settings/getsettings`,
        {
          headers:
          {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      );

      if (settingsRes.status !== 200) throw new Error('Failed to fetch company settings');
      const settings = settingsRes.data;

      const customer = customers.find(c => c.id === sale.customer_id) || {};
      const services = Array.isArray(sale.services) ? sale.services : typeof sale.services === 'string' ? JSON.parse(sale.services) : [];
      let revisedAmounts = [];

      try {
        if (Array.isArray(sale.revisedAmounts)) {
          revisedAmounts = sale.revisedAmounts;
        } else if (typeof sale.revisedAmounts === 'string') {
          revisedAmounts = JSON.parse(sale.revisedAmounts);
        } else if (Array.isArray(sale.revisedAmounts)) {
          revisedAmounts = sale.revisedAmounts;
        } else if (typeof sale.revisedAmounts === 'string') {
          revisedAmounts = JSON.parse(sale.revisedAmounts);
        }
      } catch (err) {
        console.log("Failed to parse revisedAmounts:", err);
        revisedAmounts = [];
      }



      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 20;
      const startY = 50;
      const endY = pageHeight - 40;
      let currentY = startY;

      const checkAddPage = (blockHeight = 10) => {
        if (currentY + blockHeight > endY) {
          pdf.addPage();
          currentY = startY;

          if (bgImage) {
            pdf.addImage(bgImage, "PNG", 0, 0, pageWidth, pageHeight);
          } else {
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, pageHeight, "F");
          }
        }
      };

      // Default white background first
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, "F");

      let bgImage = null;

      // Only try loading image if logo exists
      if (settings && settings.logo) {
        const bgUrl = `${process.env.REACT_APP_API_LINKS}/headers/${settings.logo}`;

        bgImage = new Image();
        bgImage.crossOrigin = "anonymous";
        bgImage.src = bgUrl;

        const bgLoaded = await new Promise((resolve) => {
          bgImage.onload = () => resolve(true);
          bgImage.onerror = () => resolve(false);
        });

        if (bgLoaded) {
          pdf.addImage(bgImage, "PNG", 0, 0, pageWidth, pageHeight);
        }
      }


      // const bgUrl = `${process.env.REACT_APP_API_LINKS}/headers/${settings?.logo || ''}`;
      // const bgImage = new Image();
      // bgImage.crossOrigin = 'anonymous';
      // bgImage.src = bgUrl;
      // const bgLoaded = await new Promise((resolve) => {
      //   bgImage.onload = () => resolve(true);
      //   bgImage.onerror = () => resolve(false);
      // });

      // if (bgLoaded) {
      //   pdf.addImage(bgImage, "PNG", 0, 0, pageWidth, pageHeight);
      // }
      // else {
      //   pdf.setFillColor(255, 255, 255);
      //   pdf.rect(0, 0, pageWidth, pageHeight, "F");
      // }

      // Header
      pdf.setFont('Times', 'bold');
      pdf.setFontSize(18);
      pdf.text('Invoice', pageWidth / 2, currentY - 10, { align: 'center' });

      // Dynamic Subject
      pdf.setFont('Times', 'bold');
      pdf.setFontSize(14);
      const subject = dynamicSubject || `Invoice for ${sale.client_name || 'Customer'}`;
      const subjectLines = pdf.splitTextToSize(subject, pageWidth - 2 * marginX);
      checkAddPage(subjectLines.length * 6);
      pdf.text(subjectLines, marginX, currentY);
      currentY += subjectLines.length * 6 + 8;

      // Customer Info
      pdf.setFont('Times', 'normal');
      pdf.setFontSize(12);
      checkAddPage(30);
      pdf.text(`To: ${sale.client_name || ''}`, marginX, currentY);
      pdf.text(`Contact: ${sale.contact_person || ''}`, marginX, currentY + 6);
      pdf.text(`Email: ${sale.email || ''}`, marginX, currentY + 12);
      pdf.text(`Phone: ${sale.phone || ''}`, marginX, currentY + 18);
      pdf.text(`Date: ${sale.invoice_date ? format(new Date(sale.invoice_date), 'dd/MM/yyyy') : new Date().toLocaleDateString()}`, pageWidth - marginX, currentY, { align: 'right' });
      currentY += 28;

      // Services Table
      const serviceColumns = ['Service Name', 'Start Date', 'End Date', 'Total Amount'];

      const serviceRows = services.map(s => [
        s.service_name || '-',
        s.start_date ? format(new Date(s.start_date), 'dd/MM/yyyy') : '-',
        s.end_date ? format(new Date(s.end_date), 'dd/MM/yyyy') : '-',
        parseFloat(s.total_amount || 0).toFixed(2)
      ]);

      autoTable(pdf, {
        startY: currentY,
        head: [serviceColumns],
        body: serviceRows,
        margin: { left: marginX, right: marginX },
        styles: {
          font: 'times',
          fontSize: 10,
          textColor: [0, 0, 0],
          cellPadding: 3,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,

        },
        alternateRowStyles: {
          fillColor: null
        },
        headStyles: {
          fontStyle: 'bold',
          halign: 'center',
          fillColor: null
        },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 35 },
          2: { cellWidth: 35 },
          3: { cellWidth: 35, halign: 'right' }
        }
      });
      currentY = pdf.lastAutoTable.finalY + 5;



      // Financial Summary
      pdf.setFont('Times', 'bold');
      pdf.text('Financial Summary:', marginX, currentY);
      pdf.setFont('Times', 'normal');

      const financialColumns = ['Description', 'Amount'];

      const financialRows = [
        ...revisedAmounts.map(entry => [
          entry.description,
          entry.description.includes('%') ? entry.amount : parseFloat(entry.amount).toFixed(2)
        ]),
        ['Previously Paid', parseFloat(sale.payingNow || 0).toFixed(2)],
        // ['Currently Paying', parseFloat(sale.currently_paying || 0).toFixed(2)],
        ['Remaining Balance', parseFloat(sale.remaining || 0).toFixed(2)]
      ];

      autoTable(pdf, {
        startY: currentY + 6,
        head: [financialColumns],
        body: financialRows,
        margin: { left: marginX, right: marginX },
        styles: {
          font: 'times',
          fontSize: 10,
          textColor: [0, 0, 0],
          cellPadding: 3,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
          fillColor: null
        },

        headStyles: {
          fontStyle: 'bold',
          halign: 'center',
          fillColor: null
        },
        alternateRowStyles: {
          fillColor: null
        },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 75, halign: 'right' }
        }
      });

      currentY = pdf.lastAutoTable.finalY + 12;
      // ================= DIGITAL SIGNATURE =================
      if (settings?.digisign) {
        const signUrl = `${process.env.REACT_APP_API_LINKS}/headers/${settings.digisign}`;

        const signImage = new Image();
        signImage.crossOrigin = "anonymous";
        signImage.src = signUrl;

        const signLoaded = await new Promise((resolve) => {
          signImage.onload = () => resolve(true);
          signImage.onerror = () => resolve(false);
        });

        if (signLoaded) {
          const signWidth = 40;
          const signHeight = 20;

          checkAddPage(40);

          currentY += 10;

          pdf.setFont("Times", "normal");
          pdf.setFontSize(11);
          pdf.text("Authorized Signature", pageWidth - marginX, currentY, { align: "right" });

          currentY += 4;

          // Signature Image
          pdf.addImage(
            signImage,
            "PNG",
            pageWidth - marginX - signWidth,
            currentY,
            signWidth,
            signHeight
          );

          currentY += signHeight + 5;
        }
      }

      if (bankData) {
        pdf.setFont("Times", "italic");
        pdf.setFontSize(10);
        const paymentNote = "You can make the payment using the following bank details:";
        checkAddPage(10);
        pdf.text(paymentNote, marginX, currentY);
        currentY += 6;

        pdf.setFont("Times", "normal");
        pdf.setFontSize(10);

        const bankDetailsRows = [
          [`Bank Name: ${bankData.bankName || "-"}`, `A/C Holder: ${bankData.accountHolderName || "-"}`],
          [`A/C Number: ${bankData.accountNumber || "-"}`, `Account Type: ${bankData.accountType || "-"}`],
          [`IFSC: ${bankData.ifscCode || "-"}`, `MICR: ${bankData.micrCode || "-"}`],
          [`Branch Name: ${bankData.branchName || "-"}`, `Branch Address: ${bankData.branchAddress || "-"}`],
        ];

        const rowHeight = 6;
        const columnWidth = (pageWidth - 2 * marginX) / 2;

        for (const row of bankDetailsRows) {
          checkAddPage(rowHeight);
          pdf.text(row[0], marginX, currentY);
          pdf.text(row[1], marginX + columnWidth, currentY);
          currentY += rowHeight;
        }

        currentY += 6;
      }

      // Output
      const fileName = `Invoice_${sale.invoice_number}_${new Date().toISOString().split('T')[0]}.pdf`;
      if (action === 'whatsapp') {
        alert('WhatsApp feature will be added soon');
      } else if (action === 'download') {
        pdf.save(fileName);
        Swal.fire('Downloaded!', '', 'success');
      } else if (action === 'preview') {
        window.open(pdf.output('bloburl'), '_blank');
      } else if (action === 'send') {
        const pdfBlob = pdf.output('blob');
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64PDF = reader.result.split(',')[1];
          const payload = {
            to: sale.email || '',
            subject: dynamicSubject || `Invoice - ${sale.client_name || 'Your Company'}`,
            message: emailContent || `Dear ${sale.contactPerson || sale.customer_name},\n\nPlease find attached the invoice details.\n\nBest regards,\n${companydata[0]?.companyname || 'Your Company'}`,
            pdfBase64: base64PDF,
            fileName,
            client: customer,
            services,
            companydata,
            settings
          };
          try {
            const emailRes = await axios.post(
              `${process.env.REACT_APP_API_LINKS}/invoice/sendinvoice`,
              payload,
              {
                headers:
                {
                  Authorization: `bearer ${token}`
                },
                withCredentials: true
              }
            );
            if (emailRes.status === 200) {
              Swal.fire('Sent!', 'Invoice has been emailed.', 'success');
            } else {
              Swal.fire('Failed!', 'Could not send email.', 'error');
            }
          } catch (err) {
            Swal.fire('Error!', 'An error occurred sending the email.', 'error');
          }
        };
        reader.readAsDataURL(pdfBlob);
      }
    } catch (err) {
      console.error('PDF generation failed:', err);
      Swal.fire('Error', 'Something went wrong!', 'error');
    }
  };

  const handleOpenEmailDialog = (sale) => {
    setSelectedSale(sale);
    const customer = customers.find(c => c.id === sale.customer_id) || {};
    setEmailSubject(`Invoice - ${new Date().toISOString().split('T')[0]}`);
    setEmailContent(
      `Dear ${customer.contactPerson || customer.name || 'Customer'},\n\n` +
      `Attached is the invoice detailing  your services and payment summary.\n\n` +
      `Please review and contact us for any queries.\n\nBest regards,\n${companydata[0]?.companyname || 'Your Company'}`
    );
    setOpenEmailDialog(true);
  };

  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
    setEmailSubject('');
    setEmailContent('');
    setSelectedSale(null);
  };

  const handleSendEmail = () => {
    if (!emailSubject.trim()) {
      Swal.fire('Error', 'Subject is required', 'error');
      return;
    }
    if (!emailContent.trim()) {
      Swal.fire('Error', 'Email content is required', 'error');
      return;
    }
    handlePdf(selectedSale, 'send', emailSubject);
    handleCloseEmailDialog();
  };

  const maildialogbox = (sale) => {
    setSelectedSale(sale);
    setInvoicedialog(true);
  };

  const closemaildialogbox = () => {
    setInvoicedialog(false);
  };

  const handleTabChange = (_, newVal) => setActiveTab(newVal);

  const filteredRows = rows.filter((row) =>
    (row.invoice_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (row.customerName || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: 3,
        fontFamily: 'Open Sans',
        backgroundColor: '#ffffff',
        minHeight: 'calc(100vh - 64px)',
        borderRadius: 3,
        boxShadow: '0px 0px 5px rgba(187,222,241,0.9)',
        mx: 'auto',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
          <Tab
            label="Sales"
            sx={{
              fontSize: { xs: '10px', sm: '12px', md: '14px' },
              minWidth: 'auto',
              px: { xs: 1, sm: 2 },
            }}
          />
          <Tab
            label="History"
            sx={{
              fontSize: { xs: '10px', sm: '12px', md: '14px' },
              minWidth: 'auto',
              px: { xs: 1, sm: 2 },
            }}
          />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              placeholder="Search by Invoice No or Customer"
              size="small"
              variant="outlined"
              sx={{
                minWidth: 300,
                width: { sm: '100%', md: 500 },
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'row', md: 'row' },
                justifyContent: { xs: 'flex-start', sm: 'space-between', md: 'flex-end' },
                alignItems: 'center',
                width: '100%',
                gap: { xs: 1, sm: 2 },
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData({
                    invoiceNumber: '',
                    invoiceDate: new Date(),
                    customerId: '',
                    createdBy: '',
                    services: [],
                    totalAmount: 0,
                    paidAmount: 0,
                    currentlyPaying: 0,
                    remainingBalance: 0,
                    finalTotalAmount: 0,
                    revisedAmounts: [],
                  });
                  setEditingSaleId(null);
                  setOpenDialog(true);
                }}
                sx={{
                  backgroundColor: '#0b85c8',
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                  fontFamily: 'Open Sans',
                  borderRadius: 2,
                }}
              >
                Add Sale
              </Button>
              <Tooltip title="Export to Excel" arrow>
                <IconButton onClick={exportToExcel}>
                  <DownloadIcon sx={{ color: '#0b85c8' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Customize Columns" arrow>
                <IconButton
                  onClick={() => setOpencolspaper(true)}
                  sx={{ color: '#0b85c8', padding: '8px' }}
                >
                  <ViewColumnIcon />
                </IconButton>
              </Tooltip>
              {opencolspaper && (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    zIndex: 1300,
                    borderRadius: 2,
                    position: 'absolute',
                    top: { lg: '100px', xs: '200px', sm: '150px', md: '150px' },
                    right: '25px',
                    width: { xs: '284px', sm: '320px', md: '280px' },
                    maxWidth: '320px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: 'Open Sans',
                        fontWeight: 600,
                        color: '#0b85c8',
                        fontSize: { xs: '13px', sm: '14px', md: '15px' },
                      }}
                    >
                      Customize Columns
                    </Typography>
                    <IconButton onClick={() => setOpencolspaper(false)} size="small">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Button
                      variant="text"
                      onClick={() => {
                        const allSelected = columns.every(column => !column.hide);
                        if (allSelected) {
                          const updatedColumns = columns.map(column => ({ ...column, hide: true }));
                          setColumns(updatedColumns);
                          setColumnOrder([]);
                        } else {
                          const updatedColumns = columns.map(column => ({ ...column, hide: false }));
                          setColumns(updatedColumns);
                          setColumnOrder(columns.map(column => column.field));
                        }
                      }}
                      sx={{
                        fontFamily: 'Open Sans',
                        fontSize: { xs: '11px', sm: '12px', md: '13px' },
                        textTransform: 'none',
                        color: '#0b85c8',
                        p: 0,
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {columns.every(column => !column.hide) ? 'Unselect All' : 'Select All'}
                    </Button>
                  </Box>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {columns.map((column) => (
                      <Box key={column.field} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Checkbox
                          checked={!column.hide}
                          onChange={(e) => {
                            const updatedColumns = columns.map((col) =>
                              col.field === column.field ? { ...col, hide: !e.target.checked } : col
                            );
                            setColumns(updatedColumns);
                            if (e.target.checked) {
                              setColumnOrder((prev) => {
                                if (!prev.includes(column.field)) {
                                  return [...prev, column.field];
                                }
                                return prev;
                              });
                            } else {
                              setColumnOrder((prev) => prev.filter(field => field !== column.field));
                            }
                          }}
                          sx={{ mr: 1, p: 0.5 }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Open Sans',
                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                          }}
                        >
                          {column.headerName || column.field}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
            </Box>
          </Box>

          {filteredRows.length === 0 ? (
            <Typography sx={{ textAlign: 'center', mt: 4, fontFamily: 'Open Sans' }}>
              No sales data available.
            </Typography>
          ) : (
            <ReusableTable
              columns={filteredColumns}
              rows={filteredRows}
              onEdit={handleEdit}
              onDelete={handleDelete}
              pdf={pdf}
              onPdf={maildialogbox}
              editicon={false}
              deleteicon={false}
              onApplyFilters={(activeFilters) => {
                setAppliedFilters(activeFilters);
              }}
              onSelectionChange={(ids) => setSelectedIds(ids)}
              onPaginationChange={handlePaginationChange}
              totalrows={totalrows}
            />
          )}

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog
              open={openDialog}
              maxWidth='sm'
              fullWidth
              sx={{
                '& .MuiPaper-root': {
                  borderRadius: 1,
                  overflow: 'hidden',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#f0f9ff',
                  py: 1,
                }}
              >
                <DialogTitle
                  sx={{
                    fontFamily: 'Open Sans',
                    fontWeight: 600,
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: '#f0f9ff',
                    display: 'flex',
                    justifyContent: 'center',
                    color: '#0b85c8',
                    fontSize: { lg: '20px', xs: '14px', sm: '16px', md: '18px' },
                    margin: 'auto',
                    lineHeight: 1.2,
                  }}
                >
                  {editingSaleId ? 'Edit Sale Invoice' : 'Create New Sale Invoice'}
                </DialogTitle>
                <IconButton
                  onClick={() => setOpenDialog(false)}
                  sx={{
                    color: '#ffff',
                    mr: 2,
                  }}
                >
                  <CloseIcon sx={{ bgcolor: '#0b85c8', borderRadius: 2 }} />
                </IconButton>
              </Box>
              <DialogContent
                sx={{
                  fontFamily: 'Open Sans',
                  backgroundColor: '#f0f9ff',
                  px: 3,
                  py: 2,
                  overflowX: 'auto',
                }}
              >
                <Box
                  sx={{
                    borderRadius: 1,
                    boxShadow: '1px 1px 10px rgba(0,0,0,0.1)',
                    padding: 2,
                    backgroundColor: '#ffffff',
                    mb: 2,
                  }}
                >
                  <Typography fontWeight={600} mb={2} ml={1}>
                    Invoice Details
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: { lg: 'row', md: 'column', xs: 'column', sm: 'column' },
                      gap: 1,
                    }}
                  >
                    <TextField
                      label="Invoice Number"
                      name="invoiceNumber"
                      value={formData.invoiceNumber}
                      size="small"
                      onChange={handleInputChange}
                      required
                      fullWidth
                      sx={{
                        width: { lg: '48%' },
                        '& .MuiInputBase-root': {
                          borderRadius: 1,
                        },
                      }}
                    />
                    <DatePicker
                      label="Invoice Date"
                      value={formData.invoiceDate}
                      onChange={handleDateChange}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          size: 'small',
                          required: true,
                          fullWidth: true,
                          sx: {
                            width: { lg: '48%' },
                            '& .MuiInputBase-root': {
                              height: 36,
                              borderRadius: 1,
                            },
                            '& .MuiInputBase-input': {
                              padding: '8px 10px',
                              height: '20px',
                            },
                          },
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    borderRadius: 1,
                    boxShadow: '1px 1px 10px rgba(0,0,0,0.1)',
                    padding: 2,
                    backgroundColor: '#ffffff',
                    mb: 2,
                  }}
                >
                  <Typography fontWeight={600} mb={2} ml={1}>
                    Customer Details
                  </Typography>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(opt) => opt.name}
                    fullWidth
                    onChange={handleCustomerSelect}
                    value={customers.find((c) => c.id === formData.customerId) || null}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Customer"
                        size="small"
                        fullWidth
                        required
                        sx={{
                          '& .MuiInputBase-root': {
                            borderRadius: 1,
                          },
                        }}
                      />
                    )}
                    renderOption={(props, opt) => (
                      <li {...props} key={opt.id}>
                        <Box>
                          <Typography>{opt.name}</Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Box>

                {formData.customerId && (
                  <Box
                    sx={{
                      borderRadius: 1,
                      boxShadow: '1px 1px 10px rgba(0,0,0,0.1)',
                      padding: 2,
                      backgroundColor: '#ffffff',
                      mb: 2,
                      overflowX: 'auto',
                    }}
                  >
                    <Typography fontWeight={600} mb={2} ml={1}>
                      Services
                    </Typography>
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                      <TableContainer
                        component={Paper}
                        sx={{ width: '98%', mt: 2, mb: 2, margin: 'auto', overflow: 'auto', minWidth: 800 }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f0f9ff' }}>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>S.No</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Service Name</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Start Date</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>End Date</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Total Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formData.services.map((service, index) => (
                              <TableRow key={index}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{service.name}</TableCell>
                                <TableCell>{service.start_date}</TableCell>
                                <TableCell>{service.end_date}</TableCell>
                                <TableCell>{service.totalAmount.toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    <Typography fontWeight={600} mb={2} ml={1} mt={4} sx={{ textAlign: 'center' }}>
                      Revised Amounts
                    </Typography>
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                      <TableContainer
                        component={Paper}
                        sx={{ width: '98%', mt: 2, mb: 2, margin: 'auto', overflow: 'auto' }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f0f9ff' }}>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Description</TableCell>
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'right' }}>Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formData.revisedAmounts.map((entry, index) => (
                              <TableRow key={index}>
                                <TableCell>{entry.description}</TableCell>
                                <TableCell sx={{ textAlign: 'right' }}>
                                  {entry.description.includes('Percentage')
                                    ? entry.amount
                                    : parseFloat(entry.amount).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell>Previously Paid</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>{formData.paidAmount.toFixed(2)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Currently Paying</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                <TextField
                                  label="Currently Paying"
                                  type="number"
                                  size="small"
                                  value={formData.currentlyPaying}
                                  onChange={(e) => handlePaymentChange(e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    step: 0.01,
                                  }}
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      borderRadius: 1,
                                    },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Remaining Balance</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>{formData.remainingBalance.toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    borderRadius: 1,
                    boxShadow: '1px 1px 10px rgba(0,0,0,0.1)',
                    padding: 2,
                    backgroundColor: '#ffffff',
                    mb: 2,
                  }}
                >
                  <Typography fontWeight={600} mb={2} ml={1}>
                    Created By
                  </Typography>
                  <TextField
                    select
                    label="Created By"
                    name="createdBy"
                    value={formData.createdBy}
                    onChange={handleInputChange}
                    size="small"
                    required
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: 1,
                      },
                    }}
                  >
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.employee_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#0b85c8',
                      fontFamily: 'Open Sans',
                      borderRadius: 2,
                      textTransform: 'none',
                      '&:disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#a0a0a0',
                      },
                      '&:hover': {
                        backgroundColor: '#0a7ab8',
                      },
                    }}
                    onClick={handleSubmit}
                  >
                    {editingSaleId ? 'Update Sale' : 'Save Sale'}
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>

            <Dialog open={invoicedialog} onClose={closemaildialogbox}>
              <DialogTitle sx={{ textAlign: 'center', fontFamily: 'Open Sans', fontSize: '18px' }}>
                What would you like to do?
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
                  <Tooltip title="Send Mail">
                    <IconButton
                      color="primary"
                      onClick={() => {
                        closemaildialogbox();
                        handleOpenEmailDialog(selectedSale);
                      }}
                    >
                      <MailOutlineIcon />
                    </IconButton>
                  </Tooltip>
                  {/* <Tooltip title="WhatsApp">
                    <IconButton
                      color="success"
                      onClick={() => {
                        handlePdf(selectedSale, 'whatsapp');
                        closemaildialogbox();
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  </Tooltip> */}
                  <Tooltip title="Preview PDF">
                    <IconButton
                      color="info"
                      onClick={() => {
                        handlePdf(selectedSale, 'preview');
                        closemaildialogbox();
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download PDF">
                    <IconButton
                      color="info"
                      onClick={() => {
                        handlePdf(selectedSale, 'download');
                        closemaildialogbox();
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton
                      color="error"
                      onClick={closemaildialogbox}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </DialogContent>
            </Dialog>

            <Dialog
              open={openEmailDialog}
              onClose={handleCloseEmailDialog}
              maxWidth="sm"
              fullWidth
              sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
            >
              <DialogTitle sx={{ fontFamily: 'Open Sans', textAlign: 'center', color: '#0b85c8', backgroundColor: '#f0f9ff' }}>
                Compose Email
              </DialogTitle>
              <DialogContent sx={{ fontFamily: 'Open Sans', backgroundColor: '#f0f9ff', pt: 2 }}>
                <TextField
                  label="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email Content"
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  fullWidth
                  multiline
                  rows={6}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center', backgroundColor: '#f0f9ff', pb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSendEmail}
                  sx={{ backgroundColor: '#0b85c8', fontFamily: 'Open Sans' }}
                >
                  Send Email
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCloseEmailDialog}
                  sx={{ fontFamily: 'Open Sans', color: '#0b85c8', borderColor: '#0b85c8' }}
                >
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>
          </LocalizationProvider>
        </>
      ) : (
        <SalesHistory />
      )}
    </Container>
  );
}


// import React from 'react';
// import { Container, Box, Typography, Paper, Button } from '@mui/material';
// import ConstructionIcon from '@mui/icons-material/Construction';

// export default function Sales() {
//   return (
//     <Container
//       sx={{
//         minHeight: '80vh',
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//       }}
//     >
//       <Paper
//         elevation={6}
//         sx={{
//           padding: 4,
//           textAlign: 'center',
//           maxWidth: 500,
//           width: '100%',
//           borderRadius: 3,
//           backgroundColor: '#f5f5f5',
//         }}
//       >
//         <Box sx={{ mb: 2 }}>
//           <ConstructionIcon sx={{ fontSize: 60, color: '#ff9800' }} />
//         </Box>
//         <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
//           Page Under Maintenance
//         </Typography>
//         <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
//           We are working hard to bring this page back online. Please check back later.
//         </Typography>
//         <Button
//           variant="contained"
//           color="primary"
//           href="/"
//           sx={{ textTransform: 'none' }}
//         >
//           Go Back Home
//         </Button>
//       </Paper>
//     </Container>
//   );
// }

